import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Label,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    FormGroup,
    Tooltip,
  } from "reactstrap";
  import DataTable from "react-data-table-component";
  import { useMemo, useState } from "react";
  import { useTranslation } from "react-i18next";
  import { MoreVertical, Eye, EyeOff, Copy } from "react-feather";
  import NavigationBreadcrumbs from "../../common-elements/breadcrumbs/NavigationBreadcrumbs";
  import CommonDropDown from "../common/CommonDropDown";
  import { tableHeaderStyles } from "../../styles/ReactSelectStyles";
  import { H4 } from "../../AbstractElements";
  import * as Yup from "yup";
  import { Formik, Form, Field, ErrorMessage } from "formik";

  // Mock data with longer API keys
  const apiKeysData = [
    {
      id: 1,
      name: "User Authentication",
      type: "Read",
      apiKey: "abc1234567890123445xyz789",
      status: "Active",
    },
    {
      id: 2,
      name: "Payment Gateway",
      type: "Write",
      apiKey: "def45678901234545678uvw012",
      status: "Active",
    },
    {
      id: 3,
      name: "Admin Access",
      type: "Admin",
      apiKey: "ghi78901234567890121rst345",
      status: "Inactive",
    },
    {
      id: 4,
      name: "Analytics Service",
      type: "Read",
      apiKey: "jkl90123456789012345mno678",
      status: "Active",
    },
    {
      id: 5,
      name: "Email Notifications",
      type: "Write",
      apiKey: "pqr12345678901234567stu901",
      status: "Active",
    },
    {
      id: 6,
      name: "Database Access",
      type: "Read",
      apiKey: "vwx45678901234567890yz123",
      status: "Inactive",
    },
    {
      id: 7,
      name: "File Storage",
      type: "Write",
      apiKey: "abc78901234567890123def456",
      status: "Active",
    },
    {
      id: 8,
      name: "User Profile Management",
      type: "Read",
      apiKey: "ghi12345678901234567jkl789",
      status: "Active",
    },
    {
      id: 9,
      name: "Order Processing",
      type: "Write",
      apiKey: "mno45678901234567890pqr012",
      status: "Inactive",
    },
    {
      id: 10,
      name: "Inventory Management",
      type: "Admin",
      apiKey: "stu78901234567890123vwx456",
      status: "Active",
    },
    {
      id: 11,
      name: "Customer Support",
      type: "Read",
      apiKey: "yz123456789012345678abc901",
      status: "Active",
    },
    {
      id: 12,
      name: "Billing System",
      type: "Write",
      apiKey: "def12345678901234567ghi234",
      status: "Active",
    },
    {
      id: 13,
      name: "Audit Logging",
      type: "Read",
      apiKey: "jkl45678901234567890mno567",
      status: "Inactive",
    },
    {
      id: 14,
      name: "Marketing Campaigns",
      type: "Write",
      apiKey: "pqr78901234567890123stu890",
      status: "Active",
    },
    {
      id: 15,
      name: "Data Export",
      type: "Read",
      apiKey: "vwx12345678901234567yz123",
      status: "Active",
    },
    {
      id: 16,
      name: "Security Monitoring",
      type: "Admin",
      apiKey: "abc45678901234567890def456",
      status: "Inactive",
    },
    {
      id: 17,
      name: "Push Notifications",
      type: "Write",
      apiKey: "ghi78901234567890123jkl789",
      status: "Active",
    },
    {
      id: 18,
      name: "Content Management",
      type: "Write",
      apiKey: "mno12345678901234567pqr012",
      status: "Active",
    },
    {
      id: 19,
      name: "User Analytics",
      type: "Read",
      apiKey: "stu45678901234567890vwx345",
      status: "Inactive",
    },
    {
      id: 20,
      name: "Payment Refunds",
      type: "Write",
      apiKey: "yz789012345678901234abc678",
      status: "Active",
    },
    {
      id: 21,
      name: "API Rate Limiting",
      type: "Admin",
      apiKey: "def12345678901234567ghi901",
      status: "Active",
    },
    {
      id: 22,
      name: "Session Management",
      type: "Read",
      apiKey: "jkl45678901234567890mno234",
      status: "Active",
    },
    {
      id: 23,
      name: "Reporting Dashboard",
      type: "Read",
      apiKey: "pqr78901234567890123stu567",
      status: "Inactive",
    },
  ];

  const ApiKeysPage = () => {
    const { t } = useTranslation();
    const [filterText, setFilterText] = useState("");
    const [filters, setFilters] = useState({
      type: "",
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [blockModalOpen, setBlockModalOpen] = useState(false);
    const [selectedAPIKey, setSelectedAPIKey] = useState<{
      id: number;
      name: string;
      status?: string;
      apiKey?: string;
    } | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showAPIKey, setShowAPIKey] = useState(false);
    const [showCopyTooltip, setShowCopyTooltip] = useState<{
        [key: number]: boolean;
      }>({});
    const [showModalCopyTooltip, setShowModalCopyTooltip] = useState(false);
    const [showGenerateTooltip, setShowGenerateTooltip] = useState(false);
    const [copyError, setCopyError] = useState<string | null>(null);
    const [copyTooltipPosition, setCopyTooltipPosition] = useState<{
      x: number;
      y: number;
      show: boolean;
      id?: number;
    }>({ x: 0, y: 0, show: false });

    // Yup validation schema
    const validationSchema = Yup.object({
      name: Yup.string()
        .trim()
        .required(t("Name is required"))
        .min(3, t("Name must be at least 3 characters")),
      type: Yup.string()
        .required(t("Type is required"))
        .oneOf(["Read", "Write", "Admin"], t("Invalid type")),
      apiKey: Yup.string()
        .required(t("API Key is required"))
        .min(1, t("API Key is required")),
    });

    const initialValues = {
      id: 0,
      name: "",
      type: "",
      apiKey: "",
    };

    const types = useMemo(
      () => [
        { value: "", label: t("All") },
        { value: "Read", label: t("Read") },
        { value: "Write", label: t("Write") },
        { value: "Admin", label: t("Admin") },
      ],
      [t]
    );

    const toggleModal = () => {
      setModalOpen((prev) => !prev);
      setIsEditing(false);
      setShowAPIKey(false);
    };

    const toggleDeleteModal = () => {
      setDeleteModalOpen((prev) => !prev);
      if (!deleteModalOpen) {
        setSelectedAPIKey(null);
      }
    };

    const toggleBlockModal = () => {
      setBlockModalOpen((prev) => !prev);
      if (!blockModalOpen) {
        setSelectedAPIKey(null);
      }
    };

    const handleDelete = (id: number, name: string) => {
      setSelectedAPIKey({ id, name });
      setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
      if (selectedAPIKey) {
        console.log(
          `Deleting API key with ID: ${selectedAPIKey.id}, Name: ${selectedAPIKey.name}`
        );
        setDeleteModalOpen(false);
        setSelectedAPIKey(null);
      }
    };

    const handleBlock = (id: number, name: string, currentStatus: string) => {
      setSelectedAPIKey({ id, name, status: currentStatus });
      setBlockModalOpen(true);
    };

    const confirmBlock = () => {
      if (selectedAPIKey) {
        const newStatus = selectedAPIKey.status === "Active" ? "Blocked" : "Active";
        console.log(
          `Updating API key ID: ${selectedAPIKey.id}, Name: ${selectedAPIKey.name} to status: ${newStatus}`
        );
        setBlockModalOpen(false);
        setSelectedAPIKey(null);
      }
    };

    const handleCopy = (id: number, apiKey: string, event?: React.MouseEvent) => {
        if (event) {
          const rect = (event.target as HTMLElement).getBoundingClientRect();
          setCopyTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
            show: true,
            id: id
          });
        }
        
        navigator.clipboard
          .writeText(apiKey)
          .then(() => {
            setShowCopyTooltip((prev) => ({ ...prev, [id]: true }));
            setCopyError(null);
            setTimeout(() => {
              setShowCopyTooltip((prev) => ({ ...prev, [id]: false }));
              setCopyTooltipPosition(prev => ({ ...prev, show: false }));
            }, 2000);
          })
          .catch(() => {
            setCopyError(t("Failed to copy API key"));
            setCopyTooltipPosition(prev => ({ ...prev, show: false }));
          });
      };

    const handleCopyAPIKey = (apiKey: string) => {
        navigator.clipboard
          .writeText(apiKey)
          .then(() => {
            setShowModalCopyTooltip(true);
            setCopyError(null);
            setTimeout(() => {
              setShowModalCopyTooltip(false);
            }, 2000);
          })
          .catch(() => {
            setCopyError(t("Failed to copy API key"));
          });
      };

    const generateAPIKey = (setFieldValue: (field: string, value: any) => void) => {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let key = "";
      for (let i = 0; i < 32; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setFieldValue("apiKey", key);
      setShowAPIKey(false);
    };

    const maskAPIKey = (key: string) => {
      if (!key) return "";
      const prefixLength = 3;
      const suffixLength = 3;
      const hiddenLength = key.length - prefixLength - suffixLength;
      if (hiddenLength <= 0) return key;
      return `${key.slice(0, prefixLength)}${"#".repeat(hiddenLength)}${key.slice(-suffixLength)}`;
    };

    const filteredItems = useMemo(() => {
      return apiKeysData.filter(
        (item) =>
          item.name.toLowerCase().includes(filterText.toLowerCase()) &&
          (!filters.type || item.type === filters.type)
      );
    }, [filterText, filters]);

    const columns = useMemo(
      () => [
        {
          name: t("Sl.No"),
          selector: (row: { id: number }) => row.id,
          sortable: true,
          center: false,
          width: "100px",
        },
        {
          name: t("Name"),
          selector: (row: { name: string }) => row.name,
          sortable: true,
          center: false,
          width:"220px",
        },
        {
          name: t("Type"),
          selector: (row: { type: string }) => row.type,
          sortable: true,
          center: false,

        },
        {
          name: t("API Key"),
          selector: (row: { apiKey: string }) => maskAPIKey(row.apiKey),
          sortable: false,
          center: false,
          width:"300px"

        },
        {
          name: t("Actions"),
          cell: (row: {
            id: number;
            name: string;
            status: string;
            type: string;
            apiKey: string;
          }) => (
            <>
              <UncontrolledDropdown>
                <DropdownToggle tag="span" className="cursor-pointer">
                  <MoreVertical size={18} />
                </DropdownToggle>
                <DropdownMenu container="body">
                  <DropdownItem
                    onClick={() => {
                      setIsEditing(true);
                      setModalOpen(true);
                     }}
                  >
                    {t("Edit")}
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => handleBlock(row.id, row.name, row.status)}
                  >
                    {row.status === "Active" ? t("Block") : t("Unblock")}
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDelete(row.id, row.name)}>
                    {t("Delete")}
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e) => handleCopy(row.id, row.apiKey, e)}
                  >
                    {t("Copy")}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </>
          ),
          center: true,
        },
      ],
      [t, showCopyTooltip]
    );

    const searchBar = useMemo(
      () => (
        <div className="d-flex align-items-center">
          <Label className="me-2">{t("Search")}:</Label>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterText(e.target.value)
            }
            type="search"
            value={filterText}
            placeholder={t("Searchbyname")}
            style={{ maxWidth: "180px" }}
          />
        </div>
      ),
      [filterText, t]
    );

    const breadcrumbLinks = [
      {
        label: t("APIKeys"),
        url: `${process.env.PUBLIC_URL}/masterdata/api-keys`,
        active: true,
      },
    ];

    return (
      <>
         <NavigationBreadcrumbs
          mainTitle={t("APIKeys")}
          parent={t("Master Data")}
          links={breadcrumbLinks}
        />
        <Container fluid>
          {/* Floating Copy Tooltip */}
          {copyTooltipPosition.show && (
            <div
              style={{
                position: 'fixed',
                top: copyTooltipPosition.y,
                left: copyTooltipPosition.x,
                transform: 'translateX(-50%)',
                backgroundColor: '#000',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                zIndex: 9999,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t("Copied to Clipboard")}
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  border: '4px solid transparent',
                  borderTopColor: '#000',
                }}
              />
            </div>
          )}
          <Row>
            <Col sm="12">
              <Card>
                <CardBody>
                  <div className="list-product-header">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>{searchBar}</div>
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <Button color="primary" onClick={toggleModal}>
                          <i className="fa fa-plus me-2" />
                          {t("New")}
                        </Button>
                        <CommonDropDown color="info" />
                      </div>
                    </div>
                  </div>
                  <div className="list-product">
                    <div className="table-responsive">
                      <DataTable
                        data={filteredItems}
                        columns={columns}
                        striped
                        highlightOnHover
                        customStyles={tableHeaderStyles}
                        pagination
                      />
                    </div>
                  </div>
                  <Modal
                    isOpen={modalOpen}
                    toggle={toggleModal}
                    className="modal-dialog modal-lg"
                    centered
                  >
                    <ModalHeader toggle={toggleModal}>
                      {isEditing ? t("Edit API Key") : t("Add API Key")}
                    </ModalHeader>
                    <ModalBody>
                      <Formik
                        initialValues={
                          isEditing && selectedAPIKey
                            ? {
                                id: selectedAPIKey.id,
                                name: selectedAPIKey.name,
                                type: apiKeysData.find(
                                  (item) => item.id === selectedAPIKey.id
                                )?.type || "",
                                apiKey: apiKeysData.find(
                                  (item) => item.id === selectedAPIKey.id
                                )?.apiKey || "",
                              }
                            : initialValues
                        }
                        validationSchema={validationSchema}
                        onSubmit={(values) => {
                          console.log(
                            isEditing ? `Updating API key:` : `Adding API key:`,
                            values
                          );
                          toggleModal();
                        }}
                      >
                        {({ values, setFieldValue, errors, touched }) => (
                          <Form className="theme-form">
                           
                            <Row>
                              <Col sm="6">
                                <FormGroup>
                                  <Label>
                                    {t("Name")} <span className="txt-danger">*</span>
                                  </Label>
                                  <Field
                                    name="name"
                                    type="text"
                                    className="form-control"
                                    placeholder={t("EnterAPIkeyname")}
                                  />
                                  <ErrorMessage
                                    name="name"
                                    component="span"
                                    className="text-danger"
                                  />
                                </FormGroup>
                              </Col>
                              <Col sm="6">
                                <FormGroup>
                                  <Label>
                                    {t("Type")} <span className="txt-danger">*</span>
                                  </Label>
                                  <Input
                                    type="select"
                                    value={values.type}
                                    onChange={(e) =>
                                      setFieldValue("type", e.target.value)
                                    }
                                    className="form-control"
                                   >
                                    <option value="">{t("SelectType")}</option>
                                    {types
                                      .filter((type) => type.value !== "")
                                      .map((type) => (
                                        <option key={type.value} value={type.value}>
                                          {type.label}
                                        </option>
                                      ))}
                                  </Input>
                                  <ErrorMessage
                                    name="type"
                                    component="span"
                                    className="text-danger"
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row className="align-items-center">
                            <Col sm="9">
                              <FormGroup>
                                <Label>{t("APIKey")} <span className="txt-danger">*</span></Label>
                                <div className="input-group">
                                  {values.apiKey && (
                                    <>
                                      <span
                                        className="input-group-text"
                                        id="copy-api-key"
                                        onClick={() => handleCopyAPIKey(values.apiKey)}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <Copy size={18} />
                                      </span>
                                      <Tooltip
                                        placement="top"
                                        target="copy-api-key"
                                        isOpen={showModalCopyTooltip}
                                        toggle={() => {}}
                                        className="bg-dark text-white"
                                        autohide={false}
                                      >
                                        {t("Copied")}
                                      </Tooltip>
                                    </>
                                  )}
                                  <Input
                                    value={
                                      showAPIKey
                                        ? values.apiKey
                                        : maskAPIKey(values.apiKey)
                                    }
                                    className="form-control"
                                    disabled={!values.apiKey}
                                    readOnly
                                  />
                                  {values.apiKey && (
                                    <span
                                      className="input-group-text"
                                      onClick={() => setShowAPIKey((prev) => !prev)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      {showAPIKey ? (
                                        <EyeOff size={18} />
                                      ) : (
                                        <Eye size={18} />
                                      )}
                                    </span>
                                  )}
                                </div>
                                <ErrorMessage
                                  name="apiKey"
                                  component="span"
                                  className="text-danger"
                                />
                              </FormGroup>
                            </Col>
                              <Col sm="3">
                                <Button
                                  color="success"
                                  className="mt-3"
                                  type="button"
                                  id="generate-api-key-btn"
                                  onClick={() => generateAPIKey(setFieldValue)}
                                >
                                  {t("Generate")}
                                </Button>
                                <Tooltip
                                  placement="top"
                                  target="generate-api-key-btn"
                                  isOpen={showGenerateTooltip}
                                  toggle={() => setShowGenerateTooltip(!showGenerateTooltip)}
                                >
                                  {t("Click to generate API key")}
                                </Tooltip>
                              </Col>
                            </Row>
                            <div className="text-end mt-3">
                              <Button
                                color="secondary"
                                className="me-2"
                                type="button"
                                onClick={toggleModal}
                              >
                                {t("Cancel")}
                              </Button>
                              <Button color="primary" type="submit">
                                {isEditing ? t("Update") : t("Save")}
                              </Button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </ModalBody>
                  </Modal>
                  <Modal
                    fade
                    centered
                    isOpen={deleteModalOpen}
                    toggle={toggleDeleteModal}
                  >
                    <ModalHeader toggle={toggleDeleteModal}>
                      {t("Delete API Key")}
                    </ModalHeader>
                    <ModalBody>
                      <div className="text-center">
                        <h5 style={{ marginBottom: "15px" }}>
                          {t("Are you sure you want to delete this API key?")}
                        </h5>
                        <H4>{selectedAPIKey?.name || "Unknown"}</H4>
                      </div>
                    </ModalBody>
                    <ModalFooter style={{ justifyContent: "center" }}>
                      <Button color="danger" onClick={toggleDeleteModal}>
                        {t("Cancel")}
                      </Button>
                      <Button color="success" onClick={confirmDelete}>
                        {t("Confirm")}
                      </Button>
                    </ModalFooter>
                  </Modal>
                  <Modal
                    fade
                    centered
                    isOpen={blockModalOpen}
                    toggle={toggleBlockModal}
                  >
                    <ModalHeader toggle={toggleBlockModal}>
                      {t(
                        selectedAPIKey?.status === "Active"
                          ? "Block API Key"
                          : "Unblock API Key"
                      )}
                    </ModalHeader>
                    <ModalBody>
                      <div className="text-center">
                        <h5 style={{ marginBottom: "15px" }}>
                          {t(
                            `Are you sure you want to ${
                              selectedAPIKey?.status === "Active"
                                ? "block"
                                : "unblock"
                            } this API key?`
                          )}
                        </h5>
                        <H4>{selectedAPIKey?.name || "Unknown"}</H4>
                      </div>
                    </ModalBody>
                    <ModalFooter style={{ justifyContent: "center" }}>
                      <Button color="danger" onClick={toggleBlockModal}>
                        {t("Cancel")}
                      </Button>
                      <Button color="success" onClick={confirmBlock}>
                        {t("Confirm")}
                      </Button>
                    </ModalFooter>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  };

  export default ApiKeysPage;